"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * A single searchable item in the async picker.
 * - `key` is what gets stored in form state (e.g. country code, interest id)
 * - `label` is the primary display text (e.g. "United States", "Running")
 * - `secondary` is optional muted text (e.g. "US", "Audience size: 1.2M")
 */
export interface AsyncPickerItem {
  key: string;
  label: string;
  secondary?: string;
}

export interface AsyncPickerFieldProps {
  label: string;
  placeholder: string;

  /** Currently selected keys (IDs stored in form state). */
  values: string[];
  onChange: (keys: string[]) => void;

  /** Search state — caller owns the query and wires it to the search hook. */
  query: string;
  onQueryChange: (q: string) => void;

  /** Normalized results from the search hook. Pass `undefined` while loading. */
  results: AsyncPickerItem[] | undefined;

  /**
   * Whether the search hook is currently fetching. Shows a spinner in the
   * input + a "Searching..." state in the dropdown. Usually wired from
   * tanstack-query's `isFetching` (covers both initial load and refetches).
   */
  isLoading?: boolean;

  /**
   * Resolve a selected key to its display label. Callers that have a static
   * lookup (e.g. Intl.DisplayNames for country codes) can implement this
   * synchronously; callers with async data (e.g. interests) should populate
   * a label map from `results` as they arrive.
   */
  getLabel: (key: string) => string;

  /**
   * Optional muted secondary text shown next to each selected pill.
   * Callers use this to surface the raw ID (e.g. Meta interest IDs) when
   * useful. Returning `undefined` hides the secondary for that pill.
   */
  getPillSecondary?: (key: string) => string | undefined;

  /**
   * When set, renders a muted empty-state pill (matching the selected-pill
   * shape, but in muted grey) whenever `values` is empty. Use it to surface
   * "no <thing> selected" — a visual hint that an optional/required slot
   * exists but isn't filled. Omit to render nothing when empty.
   */
  emptyStateLabel?: string;
  /** Minimum query length before the dropdown shows. Default: 1. */
  minQueryLength?: number;
  /**
   * When true, selecting a new item REPLACES the current selection instead
   * of appending. Used for pickers that are semantically single-select but
   * share the async-search / pill affordance of the multi-select (e.g.
   * Meta's Special Ad Category country, which Meta only honors at [0]).
   */
  singleSelect?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
}

/**
 * Generic async multi-select picker — selected items render as pills, search
 * input shows a dropdown of matching results. Used for Meta countries and
 * interests; works for any keyed async search.
 *
 * Deliberately plain HTML + Tailwind (no shadcn imports) to keep the SDK
 * decoupled from frontend-specific UI libraries.
 */
export function AsyncPickerField({
  label,
  placeholder,
  values,
  onChange,
  query,
  onQueryChange,
  results,
  isLoading = false,
  getLabel,
  getPillSecondary,
  emptyStateLabel,
  minQueryLength = 1,
  singleSelect = false,
  disabled = false,
  hasError = false,
  errorMessage,
}: AsyncPickerFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set());
  // Which item the keyboard cursor is on. -1 = nothing highlighted (when the
  // list is empty). Reset to 0 on open + whenever the result list changes.
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Hide already-selected rows from the dropdown in multi-select mode; in
  // single-select mode show everything so the user can swap the selection.
  const filteredResults = (results ?? []).filter(
    (r) => singleSelect || !values.includes(r.key),
  );

  const add = (key: string) => {
    setAdded((prev) => new Set(prev).add(key));
    // Single-select: replace the entire selection with the new key.
    onChange(singleSelect ? [key] : [...values, key]);
    onQueryChange("");
    setIsOpen(false);
    inputRef.current?.focus();
    setTimeout(() => {
      setAdded((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 150);
  };

  const remove = (key: string) => {
    setRemoving((prev) => new Set(prev).add(key));
    setTimeout(() => {
      onChange(values.filter((k) => k !== key));
      setRemoving((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 150);
  };

  // Dropdown is rendered into a portal so ancestor `overflow-hidden`
  // (e.g. CollapsibleSection's grid-row collapse trick) can't clip it.
  // Position + direction are computed from the input's bounding rect and
  // re-measured on open + scroll + resize. When there isn't enough room
  // below the input, the dropdown flips above instead.
  interface DropdownPos {
    direction: "down" | "up";
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null);

  const measure = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const DESIRED_MAX_HEIGHT = 192; // tailwind max-h-48
    const GAP = 4;
    const MARGIN = 8; // breathing room against viewport edges
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    // Flip up only when below is too cramped AND above has more room.
    // Avoids thrashing when both sides are roughly equal.
    const flipUp = spaceBelow < DESIRED_MAX_HEIGHT && spaceAbove > spaceBelow;
    if (flipUp) {
      setDropdownPos({
        direction: "up",
        bottom: vh - r.top + GAP,
        left: r.left,
        width: r.width,
        maxHeight: Math.max(
          48,
          Math.min(DESIRED_MAX_HEIGHT, spaceAbove - MARGIN),
        ),
      });
    } else {
      setDropdownPos({
        direction: "down",
        top: r.bottom + GAP,
        left: r.left,
        width: r.width,
        maxHeight: Math.max(
          48,
          Math.min(DESIRED_MAX_HEIGHT, spaceBelow - MARGIN),
        ),
      });
    }
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPos(null);
      return;
    }
    measure();
    // Re-measure on window scroll / resize and on any scrolling ancestor.
    const handler = () => measure();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      // The portal-rendered dropdown is outside containerRef; check its data-attr.
      if (
        target instanceof HTMLElement &&
        target.closest("[data-async-picker-dropdown]")
      )
        return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset the keyboard cursor to the first item whenever:
  //  - the dropdown opens
  //  - the filtered result list changes (e.g. user types another char)
  // Clamp to avoid pointing past the last item when results shrink.
  useEffect(() => {
    if (!isOpen) return;
    setHighlightedIndex((prev) => {
      if (filteredResults.length === 0) return 0;
      if (prev >= filteredResults.length) return 0;
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredResults.length]);

  // Scroll the highlighted row into view as the user arrows up/down so the
  // keyboard cursor never disappears off-screen.
  useEffect(() => {
    if (!isOpen || filteredResults.length === 0) return;
    const root = dropdownRef.current;
    if (!root) return;
    const row = root.querySelector<HTMLElement>(
      `[data-async-picker-row="${highlightedIndex}"]`,
    );
    row?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, isOpen, filteredResults.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      // Opening the list with arrow keys matches native <select> UX.
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (query.length >= minQueryLength) {
          setIsOpen(true);
          e.preventDefault();
        }
      }
      return;
    }
    const n = filteredResults.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (n === 0) return;
      setHighlightedIndex((i) => (i + 1) % n);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (n === 0) return;
      setHighlightedIndex((i) => (i - 1 + n) % n);
    } else if (e.key === "Enter") {
      if (n === 0) return;
      const item = filteredResults[highlightedIndex];
      if (item) {
        e.preventDefault();
        add(item.key);
      }
    } else if (e.key === "Escape") {
      if (isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    } else if (e.key === "Home") {
      e.preventDefault();
      if (n > 0) setHighlightedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      if (n > 0) setHighlightedIndex(n - 1);
    }
  };

  const inputClass = [
    "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors",
    "placeholder:text-muted-foreground",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-50",
    hasError ? "border-destructive" : "",
  ].join(" ");

  return (
    <div>
      <p className="text-sm font-medium mb-1.5">{label}</p>

      {values.length === 0 && emptyStateLabel && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          <span className="inline-flex items-center rounded bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
            {emptyStateLabel}
          </span>
        </div>
      )}

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {values.map((key) => {
            const pillSecondary = getPillSecondary?.(key);
            return (
              <span
                key={key}
                className={`inline-flex items-center gap-1 rounded bg-newform-purple/10 text-newform-purple px-2.5 py-0.5 text-xs font-medium transition-all duration-150 ease-in-out ${
                  removing.has(key)
                    ? "opacity-0 scale-95"
                    : added.has(key)
                      ? "animate-fade-in"
                      : "opacity-100 scale-100"
                }`}
              >
                <span>{getLabel(key)}</span>
                {pillSecondary && (
                  <span className="text-newform-purple/60 font-normal [font-variant-ligatures:none]">
                    · {pillSecondary}
                  </span>
                )}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${getLabel(key)}`}
                    className="hover:bg-newform-purple/20 rounded-full p-0.5 cursor-pointer transition-colors flex items-center justify-center"
                    onClick={() => remove(key)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {!disabled && (
        <div ref={containerRef} className="relative">
          {
            // biome-ignore lint/a11y/useSemanticElements: custom combobox follows WAI-ARIA APG; native select cannot support async search
            <input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                onQueryChange(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => {
                if (query.length >= minQueryLength) setIsOpen(true);
              }}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-expanded={isOpen}
              aria-controls="async-picker-listbox"
              aria-autocomplete="list"
              aria-activedescendant={
                isOpen && filteredResults[highlightedIndex]
                  ? `async-picker-row-${filteredResults[highlightedIndex].key}`
                  : undefined
              }
              // Reserve right-side padding for either the loading spinner
              // or the chevron affordance (both are absolute-positioned).
              className={`${inputClass} pr-9`}
            />
          }

          {/* Spinner takes precedence over chevron while fetching. */}
          {isLoading ? (
            <span
              aria-hidden="true"
              className="absolute right-3 top-1/2 -translate-y-1/2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground/60 border-t-transparent"
            />
          ) : (
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          )}

          {isOpen &&
            dropdownPos &&
            (isLoading ||
              filteredResults.length > 0 ||
              query.length >= minQueryLength) &&
            typeof document !== "undefined" &&
            createPortal(
              // biome-ignore lint/a11y/useSemanticElements: custom listbox follows WAI-ARIA APG; native select cannot support async search
              <div
                ref={dropdownRef}
                data-async-picker-dropdown
                id="async-picker-listbox"
                role="listbox"
                style={{
                  position: "fixed",
                  top: dropdownPos.top,
                  bottom: dropdownPos.bottom,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  maxHeight: dropdownPos.maxHeight,
                }}
                className="z-50 overflow-y-auto rounded-md border bg-popover shadow-md"
              >
                {isLoading && filteredResults.length === 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                    <span
                      aria-hidden="true"
                      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/60 border-t-transparent"
                    />
                    Searching…
                  </div>
                )}

                {filteredResults.map((item, idx) => {
                  const isHighlighted = idx === highlightedIndex;
                  return (
                    // biome-ignore lint/a11y/useSemanticElements: custom listbox option follows WAI-ARIA APG; native option cannot live outside select
                    <button
                      key={item.key}
                      id={`async-picker-row-${item.key}`}
                      data-async-picker-row={idx}
                      role="option"
                      aria-selected={isHighlighted}
                      type="button"
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-left cursor-pointer transition-colors ${
                        isHighlighted
                          ? "bg-newform-purple/10 text-newform-purple"
                          : "hover:bg-muted/50"
                      }`}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      onClick={() => add(item.key)}
                    >
                      <span className="truncate">{item.label}</span>
                      {item.secondary && (
                        <span className="text-xs text-muted-foreground shrink-0 [font-variant-ligatures:none]">
                          {item.secondary}
                        </span>
                      )}
                    </button>
                  );
                })}

                {!isLoading &&
                  query.length >= minQueryLength &&
                  filteredResults.length === 0 &&
                  results !== undefined && (
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      No results
                    </p>
                  )}
              </div>,
              document.body,
            )}
        </div>
      )}

      {errorMessage && (
        <p className="text-sm text-destructive mt-1">{errorMessage}</p>
      )}
    </div>
  );
}
